// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Roving focus for SegmentedControl (radiogroup / tablist): where a key moves
 * the current index `at` among `n` items. ArrowRight/ArrowDown and
 * ArrowLeft/ArrowUp wrap; Home/End jump to the ends. Returns -1 for a key it
 * does not handle (or no items), so the caller leaves the event alone.
 */
export function moveIndex(at: number, key: string, n: number): number {
  if (n <= 0) return -1;
  const from = at < 0 || at >= n ? 0 : at;
  switch (key) {
    case 'ArrowRight':
    case 'ArrowDown':
      return at < 0 ? 0 : (from + 1) % n;
    case 'ArrowLeft':
    case 'ArrowUp':
      return at < 0 ? n - 1 : (from - 1 + n) % n;
    case 'Home':
      return 0;
    case 'End':
      return n - 1;
    default:
      return -1;
  }
}

/**
 * Like `moveIndex`, but skipping the items `skip(i)` rules out (disabled
 * segments). Returns -1 if the key is not handled or every item is skipped.
 */
export function moveEnabledIndex(at: number, key: string, n: number, skip: (i: number) => boolean): number {
  let to = moveIndex(at, key, n);
  if (to < 0) return -1;
  // Home/End search inwards; arrows keep going the same way.
  const step = key === 'End' || key === 'ArrowLeft' || key === 'ArrowUp' ? -1 : 1;
  for (let tries = 0; tries < n; tries++) {
    if (!skip(to)) return to;
    to = (to + step + n) % n;
  }
  return -1;
}
